import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { notificationsSlice } from 'features/notifications/notificationsSlice';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { CostGroupEnum } from 'gql/graphql';
import { ActKc2StatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { getActKC2StatusItem } from 'pages/Project/Common/types';
import { toISOString } from 'utils';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { getDateIncludingCurrentTime } from 'utils/formatDateToLocale';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useConstructionTypes } from '../hooks/useConstructionTypes';
import { useWorkOrderObjects } from '../hooks/useWorkOrderObjects';
import { gqlCreateActKC2 } from '../workOrderQueries';
import { WorkOrderChapters } from '../workorderTypes';

import { ActKC2EditForm, ActKC2Fields } from './ActKC2EditForm';

import './AddActKC2.css';

const parseActKC2 = R.evolve({
    status: getActKC2StatusItem,
    constructionType: R.prop('description'),
});

// TODO: any
const serializeActKC2: any = R.pipe(
    R.evolve({
        status: R.prop('id'),
        seasonId: R.prop('id'),
        inflationSeasonId: R.prop('id'),
        number: Number,
        reportingStartDate: toISOString,
        reportingFinishDate: toISOString,
        date: R.ifElse(R.isNil, R.always(null), getDateIncludingCurrentTime),
    }),
    R.objOf('actKC2Input'),
);

export interface IAddActKC2ModalProps {
    workOrderId: number;
    objectId: number;
    chapter?: WorkOrderChapters;
    costGroup: CostGroupEnum;
    constructionTypeId: number;
    contractor: string;
    contractorAddress: string;
    onClose?: (hasSaved?: boolean, actId?: number) => void;
}

export const AddActKC2Modal = ({
    workOrderId,
    objectId,
    chapter = WorkOrderChapters.CHAPTER1_7,
    costGroup,
    constructionTypeId,
    contractor,
    contractorAddress,
    onClose,
}: IAddActKC2ModalProps) => {
    const dispatch = useDispatch();

    const { id: projectId, orderId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [isDirtyFields, setIsDirtyFields] = useState<boolean>(false);
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);

    const { workOrderObjects } = useWorkOrderObjects(workOrderId);

    const workOrderObjectName = useMemo(
        () => workOrderObjects.filter(({ id }: { id: number }) => id === objectId)[0].name,
        [workOrderObjects, objectId],
    );

    const [constructionTypes] = useConstructionTypes(workOrderId, false);

    const constructionType = useMemo(
        () => constructionTypes?.find(R.propEq('id', constructionTypeId)),
        [constructionTypes, constructionTypeId],
    );

    const defaultValues = useMemo(
        () =>
            parseActKC2({
                status: ActKc2StatusEnum.InProgress,
                objectId,
                chapter,
                costGroup,
                constructionType: constructionType as any,
                contractor,
                contractorAddress,
                objectName: workOrderObjectName,
            }),
        [objectId, chapter, costGroup, constructionType, contractor, contractorAddress, workOrderObjectName],
    );

    const [isSubmitted, setIsSubmitted] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);

    const handleOnEditCancel = useCallback(() => {
        if (isDirtyFields) {
            setIsOpenWarningDialog(true);
        } else {
            onClose?.();
        }
    }, [isDirtyFields, onClose]);

    const handleOnEditConfirm = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, []);

    const onSuccessCreateActKC2 = useCallback(() => {
        dispatch(
            notificationsSlice.actions.add({
                key: 'createUerActKC2Notification',
                message: 'Акт КС-2 успешно добавлен',
                status: 'success',
                autoClose: 15,
            }),
        );
    }, [dispatch]);

    const [createActKC2Data, createActKC2Status, createActKC2Errors, createActKC2] = useRequest({
        requestId: 'createActKC2',
        query: gqlCreateActKC2,
        withEffect: false,
        dependenciesUpdater: onSuccessCreateActKC2,
    });

    const analyticsData = useMemo(
        () => ({
            projectId,
            orderId: Number(orderId),
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            orderId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
        ],
    );

    const handleFormSubmit = useCallback(
        (formValues: any) => {
            dispatch(
                createActKC2(
                    serializeActKC2({
                        ...formValues,
                        [ActKC2Fields.reportingStartDate]:
                            formValues?.reportingDate?.[0] !== undefined
                                ? getDateIncludingCurrentTime(formValues.reportingDate[0])
                                : null,
                        [ActKC2Fields.reportingFinishDate]:
                            formValues?.reportingDate?.[1] !== undefined
                                ? getDateIncludingCurrentTime(formValues?.reportingDate?.[1])
                                : null,
                    }),
                ),
            );
            setIsSubmitted(true);

            sendGpnAnalyticsEvent({
                componentId: 'rim_act KC save button_pressed',
                ...analyticsData,
            });
        },
        [analyticsData, createActKC2, dispatch, sendGpnAnalyticsEvent],
    );

    const handleCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const handleCreateReport = () => {
        ExportCSVReport(createActKC2Data?.uerCreateActKC2?.validation);
    };

    useEffect(() => {
        if (
            isSubmitted &&
            createActKC2Status === RequestStatus.SUCCESS &&
            createActKC2Data?.uerCreateActKC2?.result === true
        ) {
            onClose?.(true, createActKC2Data?.uerCreateActKC2?.data?.id);

            sendGpnAnalyticsEvent({
                componentId: 'rim_act KC file_saved',
                ...analyticsData,
                costGroup: createActKC2Data?.uerCreateActKC2?.data?.costGroup,
                actKC2Status: createActKC2Data?.uerCreateActKC2?.data?.status,
                actKC2Id: createActKC2Data?.uerCreateActKC2?.data?.id,
            });
        }
    }, [
        createActKC2Status,
        createActKC2Data?.uerCreateActKC2?.result,
        isSubmitted,
        onClose,
        createActKC2Data?.uerCreateActKC2?.data?.costGroup,
        createActKC2Data?.uerCreateActKC2?.data?.status,
        createActKC2Data?.uerCreateActKC2?.data?.id,
        sendGpnAnalyticsEvent,
        analyticsData,
    ]);

    const handleCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const handleSubmitWarningDialog = useCallback(() => {
        onClose?.();
    }, [onClose]);

    return (
        <ModalDialog
            title="Реквизиты Акта КС-2"
            isOpen
            hasOverlay
            withTitleCloseButton
            onClose={handleOnEditCancel}
            onConfirm={handleOnEditConfirm}
        >
            <div id="addActKC2">
                <ActKC2EditForm
                    defaultValues={defaultValues as any}
                    onSubmit={handleFormSubmit}
                    ref={formRef}
                    constructionTypeCode={constructionType?.code}
                    setIsDirty={setIsDirtyFields}
                    workOrderObjectId={objectId}
                    isCreating
                />
            </div>
            <ErrorHandlingModal
                titleText="Добавление акта КС-2"
                isSubmitted={isSubmitted}
                statusRequest={createActKC2Status}
                dataRequest={createActKC2Data?.uerCreateActKC2}
                errorsRequest={createActKC2Errors}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
            <ConfirmationDialog
                textHeader="ОТМЕНА ДОБАВЛЕНИЯ АКТА"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                onSubmit={handleSubmitWarningDialog}
                isOpened={isOpenWarningDialog}
                onClose={handleCancelWarningDialog}
            />
        </ModalDialog>
    );
};

AddActKC2Modal.displayName = 'AddActKC2Modal';
